import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import useAccountStatements from 'features/myVaro/hooks/statementsAndForms/useAccountStatements';
import { MyVaroContext } from 'features/myVaro/provider/MyVaroContextProvider';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, StyleSheet, View } from 'react-native';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import useGetDocument from 'shared/hooks/useGetDocument/useGetDocument';
import EmptyStateScreen from 'shared/patterns/vxl/EmptyStateScreen/EmptyStateScreen';
import ErrorScreen from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import useVaroUserFlow from 'shared/utils/useVaroUserFlow/useVaroUserFlow';

const AccountStatementsSelectedMonthScreen = () => {
  const router = useRouter();
  const flow = useVaroUserFlow();
  const [documentId, setDocumentId] = useState('');

  const { t: translatedText } = useTranslation('myVaro', {
    keyPrefix: 'statementsAndForms',
  });

  const {
    setAccountStatementPDFId,
    setAccountStatementPDFTitle,
    accountStatementPDFId,
    accountStatementPDFTitle,
    selectedMonthScreenTitle,
    setSelectedMonthScreenTitle,
  } = useContext(MyVaroContext);

  const { id, year, month, category, account_type } = useLocalSearchParams<{
    id: string;
    year: string;
    month: string;
    category: string;
    account_type: string;
  }>();

  const { documentDetails, executeQuery, fetching } =
    useGetDocument(documentId);

  const {
    monthlyStatementsData,
    selectedMonthStatementsData,
    loading,
    errorModel,
  } = useAccountStatements({
    accountId: id,
    year: year,
    month: month,
  });

  //Handles Screen title if we hit the url directly or get redirected from another screen
  useEffect(() => {
    monthlyStatementsData?.forEach((statement) => {
      if (statement?.title?.toLowerCase() === month) {
        setSelectedMonthScreenTitle?.(statement.title);
      }
    });
  }, [
    selectedMonthScreenTitle,
    monthlyStatementsData,
    month,
    setSelectedMonthScreenTitle,
  ]);

  useEffect(() => {
    if (documentDetails?.document?.url && documentDetails.document.title) {
      setAccountStatementPDFId?.(documentDetails.document.url);
      setAccountStatementPDFTitle?.(documentDetails.document.title);
    }
  }, [documentDetails, setAccountStatementPDFId, setAccountStatementPDFTitle]);

  useEffect(() => {
    if (accountStatementPDFId && accountStatementPDFTitle && documentId) {
      router.push({
        pathname: `/${flow}/statements-and-forms/account-statements/[id]/[year]/[month]/statement`,
        params: {
          id,
          year,
          month,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountStatementPDFId, accountStatementPDFTitle, router]);

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title: selectedMonthScreenTitle,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      {selectedMonthStatementsData?.length ? (
        <FlatList
          data={selectedMonthStatementsData}
          renderItem={({ item }) => (
            <ListItem
              key={item?.id}
              primaryCopy={item?.dateCopy}
              onPress={() => {
                setDocumentId(item?.documentId ?? '');
                executeQuery();
              }}
              analyticsId="day-pressed"
              customAttributes={{
                account_type,
                category,
                cta: item?.title,
              }}
            />
          )}
          ItemSeparatorComponent={() => (
            <View style={styles.hairLineDivider}>
              <HairLineDivider />
            </View>
          )}
          keyExtractor={(item) => item?.id}
          removeClippedSubviews={false}
        />
      ) : (
        <View style={styles.emptyStateContainer}>
          <EmptyStateScreen
            headlineText={translatedText('accountStatements.title')}
            illustrationKey="BANK_A"
            bodyText={translatedText('accountStatements.emptyStateBodyText')}
          />
        </View>
      )}

      <ScreenTransitionOverlay
        LoadingComponent={<LoadingSpinner />}
        isLoading={loading || fetching}
        ErrorComponent={errorModel ? <ErrorScreen {...errorModel} /> : null}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    flexGrow: 1,
  },
  emptyStateContainer: {
    flexGrow: 1,
  },
  hairLineDivider: {
    marginHorizontal: theme.spacing.m,
  },
});

export default AccountStatementsSelectedMonthScreen;
